import { ChatProviderMode, MockScenarioId } from "./types";

const CHAT_MODE_QUERY_KEY = "chat";
const CHAT_SCENARIO_QUERY_KEY = "chatScenario";
const CHAT_DEV_QUERY_KEY = "chatDev";
const CHAT_DEV_QUERY_KEY_LEGACY = "chatdev";
const AI_SUMMARY_QUERY_KEY = "summary";

const CHAT_PROVIDER_MODES: ChatProviderMode[] = [
  "elevenlabs",
  "mock",
  "openai_poc",
];

const MOCK_SCENARIO_IDS: MockScenarioId[] = [
  "plain_text",
  "image_text",
  "suggestions",
  "custom_metric",
  "metric_picker",
  "custom_graph",
  "error_state",
];

const isChatProviderMode = (value: string): value is ChatProviderMode => {
  return CHAT_PROVIDER_MODES.includes(value as ChatProviderMode);
};

const isMockScenarioId = (value: string): value is MockScenarioId => {
  return MOCK_SCENARIO_IDS.includes(value as MockScenarioId);
};

const getEnvMode = (): ChatProviderMode => {
  const envValue = String(process.env.REACT_APP_CHAT_PROVIDER || "").trim();
  return isChatProviderMode(envValue) ? envValue : "elevenlabs";
};

export const getChatProviderModeOverride = (
  search: string
): ChatProviderMode | null => {
  const queryMode = new URLSearchParams(search).get(CHAT_MODE_QUERY_KEY);

  if (queryMode && isChatProviderMode(queryMode)) {
    return queryMode;
  }

  return null;
};

export const resolveChatProviderMode = (search: string): ChatProviderMode => {
  const envMode = getEnvMode();
  const queryMode = getChatProviderModeOverride(search);

  if (queryMode) {
    return queryMode;
  }

  return envMode;
};

export const appendChatModeToPath = (
  path: string,
  currentSearch: string
): string => {
  const queryMode = getChatProviderModeOverride(currentSearch);
  const isChatDevEnabled = resolveChatDevControlsOverride(currentSearch);
  const isAiSummaryEnabled = resolveAiSummaryButtonOverride(currentSearch);
  if (!queryMode) {
    if (!isChatDevEnabled && !isAiSummaryEnabled) {
      return path;
    }
  }

  const [pathname, rawQuery = ""] = path.split("?");
  const nextParams = new URLSearchParams(rawQuery);

  if (queryMode && !nextParams.get(CHAT_MODE_QUERY_KEY)) {
    nextParams.set(CHAT_MODE_QUERY_KEY, queryMode);
  }

  if (
    isChatDevEnabled &&
    !nextParams.get(CHAT_DEV_QUERY_KEY) &&
    !nextParams.get(CHAT_DEV_QUERY_KEY_LEGACY)
  ) {
    nextParams.set(CHAT_DEV_QUERY_KEY, "1");
  }

  if (isAiSummaryEnabled && !nextParams.get(AI_SUMMARY_QUERY_KEY)) {
    nextParams.set(AI_SUMMARY_QUERY_KEY, "1");
  }

  const nextQuery = nextParams.toString();
  return nextQuery ? `${pathname}?${nextQuery}` : pathname;
};

export const resolveChatScenarioOverride = (
  search: string
): MockScenarioId | null => {
  const rawValue = new URLSearchParams(search).get(CHAT_SCENARIO_QUERY_KEY);
  if (!rawValue) {
    return null;
  }

  return isMockScenarioId(rawValue) ? rawValue : null;
};

export const resolveChatDevControlsOverride = (search: string): boolean => {
  const query = new URLSearchParams(search);
  const rawValue =
    query.get(CHAT_DEV_QUERY_KEY) || query.get(CHAT_DEV_QUERY_KEY_LEGACY);
  return rawValue === "1";
};

export const resolveAiSummaryButtonOverride = (search: string): boolean => {
  return new URLSearchParams(search).get(AI_SUMMARY_QUERY_KEY) === "1";
};
